import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Clock, MapPin, Play, Square, AlertTriangle, ShieldOff, Settings, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = [
  { value: "portal_button", label: "Portal Button (trust-based)" },
  { value: "browser_gps", label: "Browser GPS check-in" },
  { value: "trust_based", label: "Trust-based (no clock-in required)" },
];

export default function TimeClock() {
  const [tab, setTab] = useState("clock");
  const [config, setConfig] = useState(null);
  const [whosIn, setWhosIn] = useState([]);
  const [entries, setEntries] = useState([]);
  const [myOpen, setMyOpen] = useState(null);
  const [methodChoice, setMethodChoice] = useState("portal_button");
  const [busy, setBusy] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const load = async () => {
    try {
      const [cfg, wi, ent] = await Promise.all([
        axios.get(`${API}/time-clock/config`),
        axios.get(`${API}/time-clock/whos-clocked-in`).catch(() => ({ data: [] })),
        axios.get(`${API}/time-clock/entries`),
      ]);
      setConfig(cfg.data);
      setWhosIn(wi.data || []);
      setEntries(ent.data || []);
      const open = (ent.data || []).find((e) => e.is_open);
      setMyOpen(open || null);
      if (cfg.data?.allowed_methods?.[0]) setMethodChoice(cfg.data.allowed_methods[0]);
    } catch (e) {
      toast.error("Failed to load time clock");
    }
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async (next) => {
    try {
      const r = await axios.put(`${API}/time-clock/config`, next);
      setConfig(r.data);
      toast.success("Config saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const requestGps = () => new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve({ denied: true });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, denied: false }),
      () => resolve({ denied: true }),
      { timeout: 8000, enableHighAccuracy: true }
    );
  });

  const clockIn = async () => {
    setBusy(true);
    try {
      let gps = { denied: false, lat: null, lng: null };
      if (methodChoice === "browser_gps") gps = await requestGps();
      const r = await axios.post(`${API}/time-clock/clock-in`, {
        method: methodChoice, lat: gps.lat, lng: gps.lng, gps_denied: gps.denied,
      });
      if (r.data.flagged_for_review) toast.warning(`Clocked in — flagged: ${r.data.flag_reason}`);
      else toast.success("Clocked in");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clock-in failed"); }
    finally { setBusy(false); }
  };

  const clockOut = async () => {
    if (!myOpen) return;
    setBusy(true);
    try {
      let gps = { lat: null, lng: null };
      if (myOpen.method === "browser_gps") {
        const g = await requestGps();
        if (!g.denied) gps = { lat: g.lat, lng: g.lng };
      }
      await axios.post(`${API}/time-clock/entries/${myOpen.id}/clock-out`, gps);
      toast.success("Clocked out");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clock-out failed"); }
    finally { setBusy(false); }
  };

  const saveOverride = async () => {
    if (!editEntry) return;
    try {
      await axios.put(`${API}/time-clock/entries/${editEntry.id}`, {
        clock_in_at: editEntry.clock_in_at, clock_out_at: editEntry.clock_out_at,
        flagged_for_review: editEntry.flagged_for_review, flag_reason: editEntry.flag_reason,
        notes: editEntry.notes,
      });
      toast.success("Entry updated");
      setEditEntry(null); load();
    } catch (e) { toast.error("Update failed"); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try { await axios.delete(`${API}/time-clock/entries/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (!config) return <div>Loading…</div>;

  const toggleMethod = (m) => {
    const cur = config.allowed_methods || [];
    const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m];
    if (next.length === 0) { toast.error("At least one method must be allowed"); return; }
    saveConfig({ ...config, allowed_methods: next });
  };

  return (
    <div className="space-y-6 animate-in" data-testid="time-clock-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Clock className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Time Clock</h1>
          <p className="text-muted-foreground">Admin-controlled. GPS is never auto-enabled.</p>
        </div>
      </div>

      {!config.enabled && (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardContent className="py-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <p className="text-sm">Time clock is currently <b>disabled</b>. Enable it in the Config tab to let employees clock in.</p>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="clock" data-testid="tab-clock"><Play className="h-4 w-4 mr-1" /> Clock</TabsTrigger>
          <TabsTrigger value="entries" data-testid="tab-entries">Entries</TabsTrigger>
          <TabsTrigger value="board" data-testid="tab-board"><Users className="h-4 w-4 mr-1" /> Live board</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><Settings className="h-4 w-4 mr-1" /> Config</TabsTrigger>
        </TabsList>

        {/* CLOCK */}
        <TabsContent value="clock">
          <Card>
            <CardHeader><CardTitle>Your status</CardTitle><CardDescription>{myOpen ? `Clocked in via ${myOpen.method} at ${new Date(myOpen.clock_in_at).toLocaleString()}` : "Not currently clocked in."}</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              {!myOpen ? (
                <div className="space-y-3">
                  <Label>Method</Label>
                  <div className="flex flex-wrap gap-2">
                    {(config.allowed_methods || []).map((m) => (
                      <button key={m} onClick={() => setMethodChoice(m)}
                        className={`px-3 py-2 rounded border text-sm ${methodChoice === m ? "border-primary bg-primary/10" : "border-muted-foreground/30"}`}
                        data-testid={`method-${m}`}>
                        {METHODS.find((x) => x.value === m)?.label || m}
                      </button>
                    ))}
                  </div>
                  <Button onClick={clockIn} disabled={busy || !config.enabled} data-testid="clock-in-btn" size="lg">
                    <Play className="h-4 w-4 mr-2" /> Clock In
                  </Button>
                  {methodChoice === "browser_gps" && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Browser will ask for location permission. If you deny, the entry is still saved but flagged for admin review.</p>
                  )}
                </div>
              ) : (
                <Button onClick={clockOut} disabled={busy} variant="destructive" data-testid="clock-out-btn" size="lg">
                  <Square className="h-4 w-4 mr-2" /> Clock Out
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ENTRIES */}
        <TabsContent value="entries">
          <Card>
            <CardHeader><CardTitle>Entries ({entries.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead>
                    <TableHead>Hours</TableHead><TableHead>Method</TableHead><TableHead>Flags</TableHead>
                    <TableHead className="text-right">Admin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} className={e.flagged_for_review ? "bg-amber-50/50" : ""}>
                      <TableCell className="font-medium">{e.employee_name}</TableCell>
                      <TableCell className="text-xs">{e.clock_in_at && new Date(e.clock_in_at).toLocaleString()}</TableCell>
                      <TableCell className="text-xs">{e.clock_out_at ? new Date(e.clock_out_at).toLocaleString() : <Badge>Open</Badge>}</TableCell>
                      <TableCell>{e.duration_hours.toFixed(2)}</TableCell>
                      <TableCell><Badge variant="outline">{e.method}</Badge></TableCell>
                      <TableCell className="text-xs">
                        {e.flagged_for_review && <Badge variant="destructive" className="mr-1"><AlertTriangle className="h-3 w-3 mr-1" />Flag</Badge>}
                        {e.gps_denied && <Badge variant="outline"><ShieldOff className="h-3 w-3 mr-1" />GPS denied</Badge>}
                        {e.admin_override && <Badge variant="secondary">Override</Badge>}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditEntry({ ...e })} data-testid={`edit-entry-${e.id}`}>Edit</Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteEntry(e.id)}>Del</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {entries.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No entries yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LIVE BOARD */}
        <TabsContent value="board">
          <Card>
            <CardHeader><CardTitle>Who&apos;s clocked in</CardTitle><CardDescription>{whosIn.length} currently on the clock.</CardDescription></CardHeader>
            <CardContent>
              <div className="grid gap-2 md:grid-cols-2">
                {whosIn.map((e) => (
                  <div key={e.id} className="border rounded p-3 flex justify-between items-center" data-testid={`board-row-${e.id}`}>
                    <div>
                      <p className="font-medium">{e.employee_name}</p>
                      <p className="text-xs text-muted-foreground">Since {new Date(e.clock_in_at).toLocaleString()} · {e.method}</p>
                    </div>
                    <Badge variant="outline">{e.duration_hours.toFixed(2)} h</Badge>
                  </div>
                ))}
                {whosIn.length === 0 && <p className="text-muted-foreground text-sm">Nobody is currently clocked in.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONFIG */}
        <TabsContent value="config">
          <Card>
            <CardHeader><CardTitle>Time Clock policy</CardTitle><CardDescription>Pick which methods are available. GPS is opt-in only.</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <label className="flex justify-between items-center cursor-pointer">
                <span>Enable Time Clock</span>
                <Switch checked={config.enabled} onCheckedChange={(v) => saveConfig({ ...config, enabled: v })} data-testid="enable-time-clock-switch" />
              </label>
              <label className="flex justify-between items-center cursor-pointer">
                <span>Require clock-in (employees MUST clock in to be paid)</span>
                <Switch checked={config.require_clock_in} onCheckedChange={(v) => saveConfig({ ...config, require_clock_in: v })} data-testid="require-clock-in-switch" />
              </label>
              <div>
                <p className="text-sm font-medium mb-2">Allowed methods</p>
                <div className="space-y-2">
                  {METHODS.map((m) => (
                    <label key={m.value} className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={(config.allowed_methods || []).includes(m.value)} onCheckedChange={() => toggleMethod(m.value)} data-testid={`method-toggle-${m.value}`} />
                      <span>{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Grace mins late before flag</Label>
                  <Input type="number" value={config.grace_minutes_late || 0} onChange={(e) => setConfig({ ...config, grace_minutes_late: parseInt(e.target.value || "0") })} onBlur={() => saveConfig(config)} />
                </div>
                <div>
                  <Label>Auto clock-out after (hours)</Label>
                  <Input type="number" step="0.5" value={config.auto_clock_out_hours || 12} onChange={(e) => setConfig({ ...config, auto_clock_out_hours: parseFloat(e.target.value || "12") })} onBlur={() => saveConfig(config)} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Admin override dialog */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit entry</DialogTitle><DialogDescription>Manual override is logged.</DialogDescription></DialogHeader>
          {editEntry && (
            <div className="space-y-2">
              <Label>Clock in (ISO)</Label>
              <Input value={editEntry.clock_in_at || ""} onChange={(e) => setEditEntry({ ...editEntry, clock_in_at: e.target.value })} />
              <Label>Clock out (ISO)</Label>
              <Input value={editEntry.clock_out_at || ""} onChange={(e) => setEditEntry({ ...editEntry, clock_out_at: e.target.value })} />
              <Label>Notes</Label>
              <Textarea value={editEntry.notes || ""} onChange={(e) => setEditEntry({ ...editEntry, notes: e.target.value })} />
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <Switch checked={!!editEntry.flagged_for_review} onCheckedChange={(v) => setEditEntry({ ...editEntry, flagged_for_review: v })} />
                <span>Flagged for review</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
            <Button onClick={saveOverride} data-testid="save-override-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
